import { baseApi } from '@alltrails/redux-helpers/baseApi';
import ConditionsOverlayMetadata from '../types/ConditionsOverlayMetadata';
import type {
  CustomRouteConditionOverlayParams,
  CustomRouteGroundConditionsOverlayParams,
  CustomRouteWeatherConditionsParams,
  CustomRouteWeatherOverviewParams,
  GroundConditionsOverlayParams,
  TrailConditionOverlayParams,
  TrailWeatherConditionsParams,
  TrailWeatherOverviewParams
} from '../types/ConditionsParams';
import GroundConditionsOverlayData from '../types/GroundConditionsOverlayData';
import TrailConditionOverlayData from '../types/TrailConditionOverlayData';
import TrailWeatherConditionsData from '../types/TrailWeatherConditionsData';
import TrailWeatherOverviewData from '../types/TrailWeatherOverviewData';

const conditionsApi = baseApi.enhanceEndpoints({}).injectEndpoints({
  endpoints: builder => ({
    getConditionsMetadata: builder.query<ConditionsOverlayMetadata[], void>({
      query: () => {
        return { url: '/api/alltrails/weather-service/v2/overlay/metadata' };
      }
    }),
    getGroundConditionsOverlayData: builder.query<GroundConditionsOverlayData, GroundConditionsOverlayParams>({
      query: ({ trailId, ...params }) => {
        return { url: `/api/alltrails/weather-service/v2/trails/${trailId}/terrain`, config: { params } };
      }
    }),
    getTrailConditionOverlayData: builder.query<TrailConditionOverlayData, TrailConditionOverlayParams>({
      query: ({ trailId, showMore, ...params }) => {
        // If boolean params are false, rtk query is stripping them from the request, so stringify them first to ensure they don't get removed
        const searchParams = { ...params, showMore: String(showMore) };
        return { url: `/api/alltrails/weather-service/v2/trails/${trailId}/overlay`, config: { params: searchParams } };
      }
    }),
    getTrailWeatherConditions: builder.query<TrailWeatherConditionsData, TrailWeatherConditionsParams>({
      query: ({ trailId, ...params }) => {
        return { url: `/api/alltrails/weather-service/v2/trails/${trailId}/conditions`, config: { params } };
      }
    }),
    getTrailWeatherOverview: builder.query<TrailWeatherOverviewData, TrailWeatherOverviewParams>({
      query: ({ trailId, ...params }) => {
        return { url: `/api/alltrails/weather-service/v2/trails/${trailId}/overview`, config: { params } };
      }
    }),
    getCustomRouteGroundConditionsOverlayData: builder.query<GroundConditionsOverlayData, CustomRouteGroundConditionsOverlayParams>({
      query: ({ customRouteId, ...params }) => {
        return { url: `/api/alltrails/weather-service/v2/custom-routes/${customRouteId}/terrain`, config: { params } };
      }
    }),
    getCustomRouteConditionOverlayData: builder.query<TrailConditionOverlayData, CustomRouteConditionOverlayParams>({
      query: ({ customRouteId, showMore, ...params }) => {
        // If boolean params are false, rtk query is stripping them from the request, so stringify them first to ensure they don't get removed
        const searchParams = { ...params, showMore: String(showMore) };
        return { url: `/api/alltrails/weather-service/v2/custom-routes/${customRouteId}/overlay`, config: { params: searchParams } };
      }
    }),
    getCustomRouteWeatherConditions: builder.query<TrailWeatherConditionsData, CustomRouteWeatherConditionsParams>({
      query: ({ customRouteId, ...params }) => {
        return { url: `/api/alltrails/weather-service/v2/custom-routes/${customRouteId}/conditions`, config: { params } };
      }
    }),
    getCustomRouteWeatherOverview: builder.query<TrailWeatherOverviewData, CustomRouteWeatherOverviewParams>({
      query: ({ customRouteId, ...params }) => {
        return { url: `/api/alltrails/weather-service/v2/custom-routes/${customRouteId}/overview`, config: { params } };
      }
    })
  })
});

export const {
  useGetConditionsMetadataQuery,
  useGetGroundConditionsOverlayDataQuery,
  useGetTrailConditionOverlayDataQuery,
  useGetTrailWeatherConditionsQuery,
  useGetTrailWeatherOverviewQuery,
  useGetCustomRouteGroundConditionsOverlayDataQuery,
  useGetCustomRouteConditionOverlayDataQuery,
  useGetCustomRouteWeatherConditionsQuery,
  useGetCustomRouteWeatherOverviewQuery
} = conditionsApi;
